import React, { useState, useEffect, useRef, useCallback } from "react";
import * as Icons from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, SectionCard, ProvenanceBadge, InfoNote, Mono } from "@/components/uikit";
import { runHct3, HCT3_DEFAULTS, seedLive, nextLiveSample } from "@/lib/hct3";
import { toast } from "sonner";

const FIELDS = [
  { key: "raw_drift", label: "raw_drift (°)", step: 0.01 },
  { key: "corrected_offset", label: "corrected_offset (°)", step: 0.01 },
  { key: "refractive_index", label: "refractive_index", step: 0.0001 },
  { key: "latency", label: "latency (ms)", step: 1 },
  { key: "jitter", label: "jitter (ms)", step: 0.1 },
  { key: "confidence", label: "confidence (0-1)", step: 0.01 },
  { key: "baseline_difference", label: "baseline_difference", step: 0.01 },
  { key: "temporal_delta", label: "temporal_delta (ms)", step: 1 },
];

const chartTip = {
  contentStyle: { background: "#0D121D", border: "1px solid #1E293B", borderRadius: 8, fontSize: 12 },
  labelStyle: { color: "#94A3B8" },
};

export default function Hct3() {
  const { settings, anchorEvent, saveHct3Run } = useApp();
  const [inputs, setInputs] = useState({ ...HCT3_DEFAULTS });
  const [run, setRun] = useState(() => runHct3({ ...HCT3_DEFAULTS }, settings.thresholds));

  // ===== Live telemetry simulator state =====
  const [running, setRunning] = useState(false);
  const [liveSeries, setLiveSeries] = useState([]);
  const [liveInd, setLiveInd] = useState(null);
  const [captured, setCaptured] = useState(null);
  const liveState = useRef(seedLive({ ...HCT3_DEFAULTS }));
  const timer = useRef(null);

  const tick = useCallback(() => {
    const { state, sample, indicators } = nextLiveSample(liveState.current, settings.thresholds);
    liveState.current = state;
    setLiveSeries((prev) => [...prev.slice(-39), sample]);
    setLiveInd(indicators);
  }, [settings.thresholds]);

  useEffect(() => {
    if (running) {
      timer.current = setInterval(tick, 1000);
      return () => clearInterval(timer.current);
    }
    return undefined;
  }, [running, tick]);

  const startLive = () => setRunning(true);
  const pauseLive = () => setRunning(false);
  const resetLive = () => {
    setRunning(false);
    liveState.current = seedLive({ ...HCT3_DEFAULTS });
    setLiveSeries([]);
    setLiveInd(null);
    setCaptured(null);
  };
  const captureState = () => {
    const last = liveSeries[liveSeries.length - 1];
    if (!last) {
      toast.error("No telemetry yet", { description: "Press START to generate a live stream first." });
      return;
    }
    setCaptured({ ...last });
    toast.success("State captured", { description: `step ${last.step} · SIMULATED telemetry` });
  };
  const anchorLive = async () => {
    const snap = captured || liveSeries[liveSeries.length - 1];
    if (!snap) {
      toast.error("No state to anchor");
      return;
    }
    const rec = await anchorEvent({
      payload: JSON.stringify({ hct3_live_sample: snap, indicators: liveInd }, null, 2),
      payload_type: "Telemetry Object",
      layer: 21,
      notes: "HCT-3 LIVE captured state (SIMULATED TELEMETRY — NOT EXTERNAL SENSOR DATA)",
      drift_status: (liveInd && liveInd.anomaly_threshold === "EXCEEDED") ? "DRIFT" : snap.raw_drift >= 1 ? "WARNING" : "NONE",
      source_tag: "simulated",
      telemetry: snap,
    });
    toast.success("Live state anchored", { description: `${rec.event_id} → Vault / Reconstruction / K-Crypto` });
  };

  const update = (k, v) => setInputs((s) => ({ ...s, [k]: v === "" ? "" : Number(v) }));
  const execute = () => {
    const clean = {};
    FIELDS.forEach((f) => (clean[f.key] = inputs[f.key] === "" ? HCT3_DEFAULTS[f.key] : Number(inputs[f.key])));
    const r = runHct3(clean, settings.thresholds);
    setRun(r);
    saveHct3Run(r);
    toast.success("HCT-3 cycles computed", { description: `Convergence ${r.cycle3.convergence_pct}%` });
  };
  const reset = () => {
    setInputs({ ...HCT3_DEFAULTS });
    const r = runHct3({ ...HCT3_DEFAULTS }, settings.thresholds);
    setRun(r);
  };

  const anchorResult = async () => {
    const payload = JSON.stringify({ hct3: { ...run.cfg }, result: run.cycle3, indicators: run.indicators }, null, 2);
    const rec = await anchorEvent({
      payload,
      payload_type: "Telemetry Object",
      layer: 21,
      notes: "HCT-3 convergence result (SIMULATED demo telemetry)",
      drift_status: run.indicators.anomaly_threshold === "EXCEEDED" ? "DRIFT" : run.indicators.l8_drift_trigger ? "WARNING" : "NONE",
      source_tag: "simulated",
      telemetry: run.cfg,
    });
    toast.success("HCT-3 result anchored", { description: rec.event_id });
  };

  const ind = run.indicators;

  return (
    <div>
      <PageHeader overline="Triple-Cycle Computation" title="HCT-3 Simulator" icon="Activity"
        subtitle="Hierarchical Convergence Through Triple-Cycle Computation. Observation → Correction → Convergence."
        actions={<ProvenanceBadge source="simulated" testId="hct3-provenance" />} />

      <InfoNote tone="warn" className="mb-6">
        <strong>All values are SIMULATED demo telemetry</strong> generated deterministically — not real sensor
        measurements. Defaults: raw_drift 2.14°, corrected_offset 0.29°, refractive_index 1.0003.
      </InfoNote>

      <SectionCard title="Live Telemetry Simulator" icon="Radio" className="mb-6" testId="hct3-live-card"
        right={
          <span className={`inline-flex items-center gap-1.5 rounded border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${running ? "border-emerald-600 bg-emerald-950 text-emerald-300" : "border-slate-600 bg-slate-800 text-slate-400"}`} data-testid="hct3-live-status">
            <span className={`h-2 w-2 rounded-full ${running ? "bg-emerald-400" : "bg-slate-500"}`} aria-hidden="true" />
            {running ? "STREAMING" : "STOPPED"}
          </span>
        }>
        <InfoNote tone="warn" className="mb-3" data-testid="hct3-sim-label">
          <strong>SIMULATED TELEMETRY — NOT EXTERNAL SENSOR DATA.</strong> Locally generated stream; no external API or sensor.
        </InfoNote>
        <div className="mb-4 flex flex-wrap gap-2">
          <Button onClick={startLive} disabled={running} className="bg-emerald-600 font-bold text-white hover:bg-emerald-500" data-testid="hct3-start-button">
            <Icons.Play className="mr-2 h-4 w-4" /> Start
          </Button>
          <Button onClick={pauseLive} disabled={!running} variant="outline" className="border-amber-800 text-amber-300 hover:bg-amber-950/40" data-testid="hct3-pause-button">
            <Icons.Pause className="mr-2 h-4 w-4" /> Pause
          </Button>
          <Button onClick={resetLive} variant="outline" className="border-slate-700 text-slate-200" data-testid="hct3-reset-live-button">
            <Icons.RotateCcw className="mr-2 h-4 w-4" /> Reset
          </Button>
          <Button onClick={captureState} variant="outline" className="border-cyan-800 text-cyan-300 hover:bg-cyan-950/40" data-testid="hct3-capture-button">
            <Icons.Camera className="mr-2 h-4 w-4" /> Capture State
          </Button>
          <Button onClick={anchorLive} variant="outline" className="border-cyan-800 text-cyan-300 hover:bg-cyan-950/40" data-testid="hct3-anchor-live-button">
            <Icons.Anchor className="mr-2 h-4 w-4" /> Anchor State
          </Button>
        </div>

        {liveInd && (
          <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5" data-testid="hct3-live-indicators">
            <Indicator label="L8 Drift Trigger" value={liveInd.l8_drift_trigger ? "ACTIVE" : "IDLE"} bad={liveInd.l8_drift_trigger} />
            <Indicator label="L24 Anchor Match" value={liveInd.l24_anchor_match ? "MATCH" : "PENDING"} good={liveInd.l24_anchor_match} />
            <Indicator label="Convergence" value={liveInd.convergence_status} good={liveInd.convergence_status === "CONVERGED"} />
            <Indicator label="Anomaly Threshold" value={liveInd.anomaly_threshold} bad={liveInd.anomaly_threshold === "EXCEEDED"} warn={liveInd.anomaly_threshold === "APPROACHING"} />
            <Indicator label="Integrity" value={liveInd.integrity_status} good />
          </div>
        )}

        {captured && (
          <div className="mb-4 rounded border border-cyan-800 bg-cyan-950/20 p-3" data-testid="hct3-captured-state">
            <p className="mb-1 font-mono text-[10px] uppercase tracking-widest text-cyan-400">Captured state (step {captured.step})</p>
            <p className="font-mono text-[11px] text-slate-300">raw_drift {captured.raw_drift}° · corrected {captured.corrected_offset}° · latency {captured.latency}ms · anomaly {captured.anomaly_score}</p>
            <p className="mt-1 text-[11px] text-slate-500">Use <Mono>Anchor State</Mono> to send it to Anchor / K-Crypto / ADA-4WM / Evidence Vault / Reconstruction.</p>
          </div>
        )}

        {liveSeries.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-500">Press <strong>Start</strong> to begin the simulated telemetry stream.</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <LiveChart title="Raw vs Corrected Drift" testId="hct3-live-chart-drift">
              <LineChart data={liveSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                <XAxis dataKey="step" stroke="#64748B" fontSize={10} />
                <YAxis stroke="#64748B" fontSize={10} />
                <Tooltip {...chartTip} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Line type="monotone" dataKey="raw_drift" stroke="#FF3D00" strokeWidth={2} dot={false} isAnimationActive={false} name="Raw drift" />
                <Line type="monotone" dataKey="corrected_offset" stroke="#00E5FF" strokeWidth={2} dot={false} isAnimationActive={false} name="Corrected" />
              </LineChart>
            </LiveChart>
            <LiveChart title="Latency & Jitter" testId="hct3-live-chart-latency">
              <LineChart data={liveSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                <XAxis dataKey="step" stroke="#64748B" fontSize={10} />
                <YAxis stroke="#64748B" fontSize={10} />
                <Tooltip {...chartTip} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Line type="monotone" dataKey="latency" stroke="#A78BFA" strokeWidth={2} dot={false} isAnimationActive={false} name="Latency (ms)" />
                <Line type="monotone" dataKey="jitter" stroke="#FFB300" strokeWidth={2} dot={false} isAnimationActive={false} name="Jitter (ms)" />
              </LineChart>
            </LiveChart>
            <LiveChart title="Anomaly Score" testId="hct3-live-chart-anomaly">
              <BarChart data={liveSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                <XAxis dataKey="step" stroke="#64748B" fontSize={10} />
                <YAxis stroke="#64748B" fontSize={10} domain={[0, 100]} />
                <Tooltip {...chartTip} />
                <Bar dataKey="anomaly_score" fill="#FFB300" radius={[2, 2, 0, 0]} isAnimationActive={false} name="Anomaly" />
              </BarChart>
            </LiveChart>
            <LiveChart title="Convergence Confidence" testId="hct3-live-chart-convergence">
              <AreaChart data={liveSeries}>
                <defs>
                  <linearGradient id="liveConv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00E676" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#00E676" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                <XAxis dataKey="step" stroke="#64748B" fontSize={10} />
                <YAxis stroke="#64748B" fontSize={10} domain={[0, 100]} />
                <Tooltip {...chartTip} />
                <Area type="monotone" dataKey="convergence" stroke="#00E676" fill="url(#liveConv)" strokeWidth={2} isAnimationActive={false} name="Convergence %" />
              </AreaChart>
            </LiveChart>
          </div>
        )}
      </SectionCard>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <SectionCard title="Manual Cycle Analysis" icon="SlidersHorizontal" testId="hct3-inputs-card">
          <div className="grid grid-cols-2 gap-3">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <Label htmlFor={`hct3-${f.key}`} className="mb-1 block text-[11px] text-slate-400">{f.label}</Label>
                <Input id={`hct3-${f.key}`} data-testid={`hct3-input-${f.key}`} type="number" step={f.step}
                  value={inputs[f.key]} onChange={(e) => update(f.key, e.target.value)}
                  className="h-9 border-slate-700 bg-slate-950 font-mono text-xs text-cyan-200" />
              </div>
            ))}
          </div>
          <div className="mt-4 flex gap-2">
            <Button onClick={execute} className="flex-1 bg-cyan-500 font-bold text-slate-950 hover:bg-cyan-400" data-testid="hct3-run-button">
              <Icons.Play className="mr-2 h-4 w-4" /> Run Cycles
            </Button>
            <Button onClick={reset} variant="outline" className="border-slate-700 text-slate-200" data-testid="hct3-reset-button">Reset</Button>
          </div>
          <Button onClick={anchorResult} variant="outline" className="mt-2 w-full border-cyan-800 text-cyan-300 hover:bg-cyan-950/40" data-testid="hct3-anchor-button">
            <Icons.Anchor className="mr-2 h-4 w-4" /> Anchor Result to Chain
          </Button>
        </SectionCard>

        <div className="space-y-6 lg:col-span-2">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <CycleCard title={run.cycle1.label} tone="sky" rows={[
              ["baseline raw_drift", run.cycle1.baseline_raw_drift],
              ["refractive_index", run.cycle1.refractive_index],
              ["measurements", run.cycle1.measurements_captured],
            ]} />
            <CycleCard title={run.cycle2.label} tone="amber" rows={[
              ["baseline_difference", run.cycle2.baseline_difference],
              ["corrected_offset", run.cycle2.corrected_offset],
              ["temporal_delta", run.cycle2.temporal_delta],
            ]} />
            <CycleCard title={run.cycle3.label} tone="emerald" rows={[
              ["corrected_value", run.cycle3.corrected_value],
              ["convergence", `${run.cycle3.convergence_pct}%`],
              ["confidence", run.cycle3.confidence],
            ]} />
          </div>

          <SectionCard title="Indicators" icon="Gauge" testId="hct3-indicators-card">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
              <Indicator label="L8 Drift Trigger" value={ind.l8_drift_trigger ? "ACTIVE" : "IDLE"} bad={ind.l8_drift_trigger} />
              <Indicator label="L24 Anchor Match" value={ind.l24_anchor_match ? "MATCH" : "PENDING"} good={ind.l24_anchor_match} />
              <Indicator label="Convergence" value={ind.convergence_status} good={ind.convergence_status === "CONVERGED"} />
              <Indicator label="Anomaly Threshold" value={ind.anomaly_threshold} bad={ind.anomaly_threshold === "EXCEEDED"} warn={ind.anomaly_threshold === "APPROACHING"} />
              <Indicator label="Reconstruction" value={ind.reconstruction_available ? "AVAILABLE" : "N/A"} good={ind.reconstruction_available} />
            </div>
          </SectionCard>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SectionCard title="Corrected vs Raw Drift" icon="LineChart" testId="hct3-chart-drift">
          <ChartWrap>
            <LineChart data={run.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis dataKey="step" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} />
              <Tooltip {...chartTip} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="raw" stroke="#FF3D00" strokeWidth={2} dot={false} name="Raw drift" />
              <Line type="monotone" dataKey="corrected" stroke="#00E5FF" strokeWidth={2} dot={false} name="Corrected" />
            </LineChart>
          </ChartWrap>
        </SectionCard>

        <SectionCard title="Convergence Over Cycles" icon="TrendingUp" testId="hct3-chart-convergence">
          <ChartWrap>
            <AreaChart data={run.series}>
              <defs>
                <linearGradient id="convGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00E676" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#00E676" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis dataKey="step" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} domain={[0, 100]} />
              <Tooltip {...chartTip} />
              <Area type="monotone" dataKey="convergence" stroke="#00E676" fill="url(#convGrad)" strokeWidth={2} name="Convergence %" />
            </AreaChart>
          </ChartWrap>
        </SectionCard>

        <SectionCard title="Anomaly Score" icon="AlertTriangle" testId="hct3-chart-anomaly">
          <ChartWrap>
            <BarChart data={run.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis dataKey="step" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} domain={[0, 100]} />
              <Tooltip {...chartTip} />
              <Bar dataKey="anomaly" fill="#FFB300" radius={[3, 3, 0, 0]} name="Anomaly score" />
            </BarChart>
          </ChartWrap>
        </SectionCard>

        <SectionCard title="Integrity Status" icon="ShieldCheck" testId="hct3-integrity-card">
          <div className="flex h-[220px] flex-col items-center justify-center gap-3 text-center">
            <Icons.ShieldCheck className="h-12 w-12 text-emerald-400" aria-hidden="true" />
            <p className="font-heading text-lg font-bold uppercase text-emerald-300">Simulator Integrity OK</p>
            <p className="max-w-xs text-xs text-slate-400">
              HCT-3 computation is deterministic and reproducible. Anchoring a result creates a LOCAL evidence record
              tagged as SIMULATED telemetry.
            </p>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

function ChartWrap({ children }) {
  return <div style={{ width: "100%", height: 220 }}><ResponsiveContainer>{children}</ResponsiveContainer></div>;
}

function LiveChart({ title, testId, children }) {
  return (
    <div className="rounded-md border border-slate-800 bg-slate-950/40 p-2" data-testid={testId}>
      <p className="mb-1 px-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">{title}</p>
      <div style={{ width: "100%", height: 180 }}><ResponsiveContainer>{children}</ResponsiveContainer></div>
    </div>
  );
}

function CycleCard({ title, rows, tone }) {
  const tones = { sky: "border-sky-800 text-sky-300", amber: "border-amber-800 text-amber-300", emerald: "border-emerald-800 text-emerald-300" };
  return (
    <div className={`rounded-lg border bg-[#0D121D] p-3 ${tones[tone]}`}>
      <p className="mb-2 font-heading text-xs font-bold uppercase tracking-wide">{title}</p>
      <dl className="space-y-1">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-2">
            <dt className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{k}</dt>
            <dd className="font-mono text-xs text-slate-200">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Indicator({ label, value, good, bad, warn }) {
  const cls = bad ? "border-rose-600 text-rose-300" : warn ? "border-amber-600 text-amber-300" : good ? "border-emerald-600 text-emerald-300" : "border-slate-700 text-slate-300";
  return (
    <div className={`rounded-md border bg-slate-950/50 p-2.5 ${cls}`}>
      <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className="mt-1 font-mono text-xs font-bold uppercase tracking-wide">{value}</p>
    </div>
  );
}
